import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from './hooks/useTour';
import { useElementPosition } from './hooks/useElementPosition';
import Spotlight from './Spotlight';
import TourTooltip from './TourTooltip';
import { usePdfEditorStore } from '@/store';
import { useOnboardingStore } from '@/store/onboarding.store';
import { IS_DEV } from '@/constants/constants';

interface TourProviderProps {
  children: React.ReactNode;
  /** Z-index for tour overlay */
  zIndex?: number;
  /** Overlay opacity */
  overlayOpacity?: number;
}

/**
 * TourProvider component that renders the tour UI
 * Wrap your app with this component to enable tours
 */
export const TourProvider: React.FC<TourProviderProps> = ({
  children,
  zIndex = 9998,
  overlayOpacity = 0.75,
}) => {
  const {
    isTourActive,
    currentStep,
    isFirstStep,
    isLastStep,
    progress,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    status,
    showProgress,
    allowSkip,
  } = useTour();

  // Track target element position
  const {
    rect: targetRect,
    element: targetElement,
    isElementFound,
    scrollIntoView,
  } = useElementPosition(currentStep?.target ?? null, {
    padding: currentStep?.spotlightPadding ?? 8,
    enabled: isTourActive && status === 'running',
  });

  // Scroll target into view when step changes
  useEffect(() => {
    if (!isTourActive || !currentStep?.target) return;

    // Give DOM time to update, then scroll
    const timer = setTimeout(() => {
      if (isElementFound) {
        scrollIntoView();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStep?.id, isTourActive, isElementFound, scrollIntoView]);

  // Wait for element if specified
  useEffect(() => {
    if (!isTourActive || !currentStep?.waitForElement || isElementFound) return;

    // Poll for element if not found
    const interval = setInterval(() => {
      const el = document.querySelector(
        currentStep.target?.startsWith('[') || currentStep.target?.startsWith('.') || currentStep.target?.startsWith('#')
          ? currentStep.target
          : `[data-tour="${currentStep.target}"]`
      );
      if (el) {
        clearInterval(interval);
      }
    }, 500);

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      console.warn(`Tour step "${currentStep.id}" target "${currentStep.target}" not found after 10s`);
      // Skip to next step or complete tour
      if (isLastStep) {
        completeTour();
      } else {
        nextStep();
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [currentStep, isTourActive, isElementFound, isLastStep, nextStep, completeTour]);

  // Handle action-based advancement
  useEffect(() => {
    if (!isTourActive || !currentStep?.nextOnAction || !targetElement) return;

    const handleAction = () => {
      nextStep();
    };

    const eventType = currentStep.nextOnAction;
    targetElement.addEventListener(eventType, handleAction);

    return () => {
      targetElement.removeEventListener(eventType, handleAction);
    };
  }, [isTourActive, currentStep, targetElement, nextStep]);

  // Watch for field drops (for interactive steps)
  const prevFieldCountRef = useRef<number | null>(null);
  const currentStepIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isTourActive || !currentStep?.waitForFieldDrop) {
      prevFieldCountRef.current = null;
      currentStepIdRef.current = null;
      return;
    }

    // Get fresh field count directly from store state
    const getFieldCount = () => {
      const state = usePdfEditorStore.getState();
      const { files, activeFileId } = state.placeholders;
      const activeFile = files.find((file) => file._id === activeFileId);
      return activeFile?.fields?.length ?? 0;
    };

    // Reset and initialize when step changes
    if (currentStepIdRef.current !== currentStep.id) {
      currentStepIdRef.current = currentStep.id;
      prevFieldCountRef.current = getFieldCount();
    }

    // Poll for field additions
    const interval = setInterval(() => {
      const currentCount = getFieldCount();

      if (prevFieldCountRef.current !== null && currentCount > prevFieldCountRef.current) {
        // Field was added! Advance to next step
        clearInterval(interval);

        // Small delay to let the user see the field placed
        setTimeout(() => {
          nextStep();
        }, 500);
      }
    }, 200);

    return () => {
      clearInterval(interval);
    };
  }, [isTourActive, currentStep?.waitForFieldDrop, currentStep?.id, nextStep]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      completeTour();
    } else {
      nextStep();
    }
  }, [isLastStep, completeTour, nextStep]);

  const handlePrev = useCallback(() => {
    prevStep();
  }, [prevStep]);

  const handleSkip = useCallback(() => {
    skipTour();
  }, [skipTour]);

  // Dev mode keyboard shortcut to trigger tour (Ctrl/Cmd + Shift + T)
  useEffect(() => {
    if (!IS_DEV) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        // Force start the tour for the current context (checks active page)
        const path = window.location.pathname;
        const tourId = path.includes('word-editor') ? 'word-editor' : 'pdf-editor';
        useOnboardingStore.getState().forceStartTour(tourId);
        console.log(`[Tour] Dev shortcut: Force starting ${tourId} tour`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Don't render anything if no tour is active
  if (!isTourActive || status !== 'running') {
    return <>{children}</>;
  }

  const showSpotlight = currentStep && !currentStep.disableSpotlight && currentStep.target;

  return (
    <>
      {children}
      {createPortal(
        <>
          {/* Spotlight overlay */}
          {showSpotlight && (
            <Spotlight
              targetRect={targetRect}
              isVisible={isTourActive}
              onOverlayClick={handleSkip}
              closeOnOverlayClick={false}
              overlayOpacity={overlayOpacity}
              zIndex={zIndex}
              allowTargetInteraction={currentStep?.waitForFieldDrop}
              spotlightArea={currentStep?.spotlightArea}
            />
          )}

          {/* Tooltip */}
          <TourTooltip
            step={currentStep}
            targetElement={targetElement}
            targetRect={targetRect}
            isVisible={isTourActive}
            progress={progress}
            showProgress={showProgress}
            allowSkip={allowSkip}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            onNext={handleNext}
            onPrev={handlePrev}
            onSkip={handleSkip}
            zIndex={zIndex + 1}
          />
        </>,
        document.body
      )}
    </>
  );
};

export default TourProvider;
